import { libellePrixTotal } from '../../utils/honorairesModel';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { formatPriceFR, formatSurfaceFR } from '../../utils/formatFR';
import { getMediaUrl } from '../../utils/mediaUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * SharedPropertyDetail - Page de détail d'un bien dans une collection partagée
 */
const SharedPropertyDetail = () => {
  const { token, propertyId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [property, setProperty] = useState(null);
  const [collectionTitle, setCollectionTitle] = useState('');
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactForm, setContactForm] = useState({
    nom: '',
    email: '',
    telephone: '',
    type_demande: 'visite',
    message: ''
  });
  const [sendingContact, setSendingContact] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);

  useEffect(() => {
    const loadProperty = async () => {
      try {
        console.log('🔍 Chargement bien:', propertyId, 'de la collection:', token);
        
        // Charger la collection pour obtenir le bien
        const collectionResponse = await axios.get(`${API}/shared-collections/${token}`);
        setCollectionTitle(collectionResponse.data.title);
        
        // Trouver le bien dans la collection
        const foundProperty = collectionResponse.data.properties.find(p => p.id === propertyId);
        
        if (foundProperty) {
          setProperty(foundProperty);
          console.log('✅ Bien chargé:', foundProperty.bien_general?.titre_annonce);
        } else {
          console.error('❌ Bien non trouvé dans la collection');
        }
        
        setLoading(false);
      } catch (err) {
        console.error('❌ Erreur chargement bien:', err);
        setLoading(false);
      }
    };

    if (token && propertyId) {
      loadProperty();
    }
  }, [token, propertyId]);
  // Fonction pour envoyer le formulaire de contact
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!contactForm.nom || !contactForm.email || !contactForm.message) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    setSendingContact(true);
    
    try {
      const response = await axios.post(`${API}/contact-agence`, {
        ...contactForm,
        // lot AP — le serveur résout le bien (référence, titre, agence) à partir de cet
        // identifiant : plus de champs `bien_*` envoyés par le client.
        property_id: propertyId,
        collection_token: token
      });
      
      if (response.status === 200) {
        setContactSuccess(true);
        setTimeout(() => {
          setShowContactModal(false);
          setContactSuccess(false);
          setContactForm({
            nom: '',
            email: '',
            telephone: '',
            type_demande: 'visite',
            message: ''
          });
        }, 3000);
      }
    } catch (error) {
      console.error('Erreur envoi contact:', error);
      alert('Erreur lors de l\'envoi du message. Veuillez réessayer.');
    } finally {
      setSendingContact(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-5xl text-blue-600 mb-4 animate-spin">
            <i className="bi bi-hourglass-split"></i>
          </div>
          <p className="text-gray-600">Chargement du bien...</p>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="text-5xl text-red-500 mb-4">
            <i className="bi bi-exclamation-triangle"></i>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Bien introuvable</h2>
          <p className="text-gray-600 mb-6">Ce bien n'existe pas dans cette collection.</p>
          <button
            onClick={() => navigate(`/shared/${token}`)}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold"
          >
            Retour à la collection
          </button>
        </div>
      </div>
    );
  }

  // Extraire les données
  const photos = property.medias?.photos || property.photos || [];
  
  const titre = property.bien_general?.titre_annonce || 
               property.enseigne?.enseigne || 
               property.bien_general?.type_bien ||
               'Bien immobilier';
               
  const typeBien = property.bien_general?.type_bien || property.type_bien || '';
  
  const ville = property.bien_general?.ville_generale ||
               property.informations_confidentielles?.ville || 
               property.ville || 
               '';
               
  const codePostal = property.informations_confidentielles?.code_postal || '';
  const adresse = property.informations_confidentielles?.adresse || '';
  
  const prix = property.honoraires_calcul?.prix_vente ||
              property.bien_general?.prix_vente || 
              property.bien_general?.prix_estime ||
              property.informations_vente?.prix ||
              property.prix_vente || 
              property.prix_estime ||
              0;
              
  const surface = property.composition?.superficie_totale ||
                 property.composition?.superficie ||
                 property.composition?.surface_totale ||
                 property.bien_general?.surface_totale || 
                 property.surface_totale || 
                 0;
                 
  const nbPieces = property.composition?.nombre_pieces ||
                  property.bien_general?.nombre_pieces || 
                  property.nombre_pieces || 
                  0;
  
  // Description
  let description = '';
  if (property.bien_general?.descriptif) {
    const desc = property.bien_general.descriptif;
    description = typeof desc === 'string' ? desc : (desc.descriptions?.fr || desc.descriptions?.en || '');
  }

  // Caractéristiques diverses - essayer plusieurs sources
  const activitePrincipale = property.bien_general?.activite_principale || '';
  const numeroMandat = property.numero_mandat || '';
  const statutBien = property.bien_general?.statut_bien || 'Libre';
  const disponibilite = property.bien_general?.disponibilite || property.disponibilite || '';
  
  const surfaceVente = property.composition?.surface_vente || 0;
  const surfaceCuisine = property.composition?.surface_cuisine ||
                        property.bien_general?.surface_cuisine || 
                        0;
  const reserve = property.composition?.reserve ||
                 property.bien_general?.reserve || 
                 0;
  const nbCouverts = property.composition?.nombre_couverts_total ||
                    property.composition?.nombre_couverts ||
                    property.bien_general?.nombre_couverts || 
                    0;
  const placesInterieur = property.composition?.places_assises_interieur ||
                         property.composition?.places_interieur ||
                         property.bien_general?.places_interieur || 
                         0;
  const placesTerrasse = property.composition?.places_assises_terrasse ||
                        property.composition?.places_terrasse ||
                        property.bien_general?.places_terrasse || 
                        0;
  const surfaceTerrasse = property.composition?.terrasse_m2 ||
                         property.composition?.surface_terrasse ||
                         property.bien_general?.surface_terrasse || 
                         0;
  const nbSalles = property.composition?.nombre_salles || 0;
  const nbEmployes = property.composition?.nombre_employes || 0;
  
  // Informations bail
  const loyerMensuel = property.informations_bail?.loyer_mensuel || 0;
  const charges = property.informations_bail?.charges || 0;
  
  // Honoraires
  const netVendeur = property.honoraires_calcul?.net_vendeur || 0;
  const honoraires = property.honoraires_calcul?.honoraires || 0;
  // lot1 — libellé du prix « tout compris » : source unique (`honorairesModel.js`).
  const typePrix = libellePrixTotal(property.informations_vente?.honoraires_charge
    || property.honoraires_calcul?.honoraires_charge);
  
  // Descriptif
  const descriptif = property.descriptif?.descriptions?.['Fiche Technique']?.Français?.content || 
                    property.descriptif?.descriptions?.['fiche_technique']?.Français?.content || '';
  
  // Packs actifs
  const packsActifs = property.packs_actifs || {};
  
  // Charger les packs
  const getPackData = (packId) => {
    const packs = {
      'restauration': require('../../configs/packs/pack-restauration-complet.json'),
      'retail': require('../../configs/packs/pack-retail-complet.json'),
      'enseignes': require('../../configs/packs/pack-enseignes.json')
    };
    return packs[packId] || null;
  };
  
  // Extraire les valeurs des champs des packs
  const getPackFieldValue = (field, property) => {
    const path = field.groupe_data?.split('.') || [];
    let value = property;
    
    for (const key of path) {
      value = value?.[key];
      if (value === undefined) break;
    }
    
    return value;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header avec retour */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => navigate(`/shared/${token}`)}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
          >
            <i className="bi bi-arrow-left"></i>
            Retour à {collectionTitle}
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Photos */}
          {photos.length > 0 && (
            <div className="relative h-96 bg-gray-200">
              <img
                src={getMediaUrl(photos[currentPhotoIndex], 'large', token)}
                alt={titre}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = '/placeholder-property.jpg';
                }}
              />
              
              {/* Navigation photos */}
              {photos.length > 1 && (
                <>
                  <button
                    onClick={() => setCurrentPhotoIndex((currentPhotoIndex - 1 + photos.length) % photos.length)}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg"
                  >
                    <i className="bi bi-chevron-left text-xl"></i>
                  </button>
                  <button
                    onClick={() => setCurrentPhotoIndex((currentPhotoIndex + 1) % photos.length)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg"
                  >
                    <i className="bi bi-chevron-right text-xl"></i>
                  </button>
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-4 py-2 rounded-full text-sm">
                    {currentPhotoIndex + 1} / {photos.length}
                  </div>
                </>
              )}
            </div>
          )}

          <div className="p-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Colonne principale */}
              <div className="lg:col-span-2">
                {/* Titre */}
                <h1 className="text-3xl font-bold text-gray-900 mb-4">{titre}</h1>
                
                {/* Type, activité et localisation */}
                <div className="flex flex-wrap gap-3 mb-6">
                  {typeBien && (
                    <span className="bg-blue-100 text-blue-800 px-4 py-2 rounded-lg font-medium flex items-center gap-2">
                      <i className="bi bi-shop"></i>
                      {typeBien}
                    </span>
                  )}
                  {activitePrincipale && (
                    <span className="bg-orange-100 text-orange-800 px-4 py-2 rounded-lg font-medium flex items-center gap-2">
                      <i className="bi bi-briefcase"></i>
                      {activitePrincipale}
                    </span>
                  )}
                  {numeroMandat && (
                    <span className="bg-blue-100 text-blue-800 px-4 py-2 rounded-lg font-medium flex items-center gap-2">
                      <i className="bi bi-tag-fill"></i>
                      Réf: {numeroMandat}
                    </span>
                  )}
                  {statutBien && (
                    <span className="bg-green-100 text-green-800 px-4 py-2 rounded-lg font-medium flex items-center gap-2">
                      <i className="bi bi-check-circle-fill"></i>
                      {statutBien}
                    </span>
                  )}
                </div>
                
                {/* Localisation */}
                <div className="flex items-center gap-2 text-gray-700 mb-6 bg-gray-50 px-4 py-3 rounded-lg">
                  <i className="bi bi-geo-alt-fill text-red-500 text-xl"></i>
                  <span className="font-medium">{adresse ? `${adresse}, ` : ''}{ville} {codePostal}</span>
                </div>

                {/* Prix et surface */}
                <div className="flex items-center gap-8 mb-8 pb-8 border-b">
                  {prix > 0 && (
                    <div>
                      <div className="text-sm text-gray-500 mb-1">Prix de vente</div>
                      <div className="text-4xl font-bold text-emerald-600">
                        {formatPriceFR(prix)}
                      </div>
                    </div>
                  )}
                  {surface > 0 && (
                    <div>
                      <div className="text-sm text-gray-500 mb-1">Surface</div>
                      <div className="text-3xl font-bold text-gray-900 flex items-center gap-2">
                        <i className="bi bi-arrows-angle-expand text-blue-600"></i>
                        {formatSurfaceFR(surface)}
                      </div>
                    </div>
                  )}
                </div>

                {/* Description */}
                {description && (
                  <div className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Description</h2>
                    <p className="text-gray-700 whitespace-pre-line leading-relaxed">
                      {description}
                    </p>
                  </div>
                )}

                {/* 1. Descriptif */}
                {descriptif && (
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <i className="bi bi-file-text text-blue-600"></i>
                      Descriptif
                    </h2>
                    <div className="bg-white p-6 rounded-xl border border-gray-200">
                      <p className="text-gray-700 whitespace-pre-line leading-relaxed">
                        {descriptif}
                      </p>
                    </div>
                  </div>
                )}

                {/* 2. Caractéristiques */}
                <div className={descriptif ? "mt-8" : ""}>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <i className="bi bi-list-check text-blue-600"></i>
                    Caractéristiques
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-gray-50 p-6 rounded-xl">
                    {surface > 0 && (
                      <div className="flex justify-between items-center py-3 px-4 bg-white rounded-lg">
                        <span className="text-gray-600 flex items-center gap-2">
                          <i className="bi bi-rulers text-teal-500"></i>
                          Superficie totale
                        </span>
                        <span className="font-bold text-gray-900">{formatSurfaceFR(surface)}</span>
                      </div>
                    )}
                    {surfaceVente > 0 && (
                      <div className="flex justify-between items-center py-3 px-4 bg-white rounded-lg">
                        <span className="text-gray-600 flex items-center gap-2">
                          <i className="bi bi-shop-window text-blue-500"></i>
                          Surface de vente
                        </span>
                        <span className="font-bold text-gray-900">{formatSurfaceFR(surfaceVente)}</span>
                      </div>
                    )}
                    {surfaceCuisine > 0 && (
                      <div className="flex justify-between items-center py-3 px-4 bg-white rounded-lg">
                        <span className="text-gray-600 flex items-center gap-2">
                          <i className="bi bi-egg-fried text-orange-500"></i>
                          Surface cuisine
                        </span>
                        <span className="font-bold text-gray-900">{formatSurfaceFR(surfaceCuisine)}</span>
                      </div>
                    )}
                    {reserve > 0 && (
                      <div className="flex justify-between items-center py-3 px-4 bg-white rounded-lg">
                        <span className="text-gray-600 flex items-center gap-2">
                          <i className="bi bi-box-seam text-slate-500"></i>
                          Réserve
                        </span>
                        <span className="font-bold text-gray-900">{formatSurfaceFR(reserve)}</span>
                      </div>
                    )}
                    {nbPieces > 0 && (
                      <div className="flex justify-between items-center py-3 px-4 bg-white rounded-lg">
                        <span className="text-gray-600 flex items-center gap-2">
                          <i className="bi bi-door-open text-indigo-500"></i>
                          Nombre de pièces
                        </span>
                        <span className="font-bold text-gray-900">{nbPieces}</span>
                      </div>
                    )}
                    {nbSalles > 0 && (
                      <div className="flex justify-between items-center py-3 px-4 bg-white rounded-lg">
                        <span className="text-gray-600 flex items-center gap-2">
                          <i className="bi bi-grid-3x3 text-pink-500"></i>
                          Nombre de salles
                        </span>
                        <span className="font-bold text-gray-900">{nbSalles}</span>
                      </div>
                    )}
                    {nbEmployes > 0 && (
                      <div className="flex justify-between items-center py-3 px-4 bg-white rounded-lg">
                        <span className="text-gray-600 flex items-center gap-2">
                          <i className="bi bi-people-fill text-green-500"></i>
                          Nombre d'employés
                        </span>
                        <span className="font-bold text-gray-900">{nbEmployes}</span>
                      </div>
                    )}
                    {nbCouverts > 0 && (
                      <div className="flex justify-between items-center py-3 px-4 bg-white rounded-lg">
                        <span className="text-gray-600 flex items-center gap-2">
                          <i className="bi bi-cup-hot text-brown-500"></i>
                          Nombre de couverts
                        </span>
                        <span className="font-bold text-gray-900">{nbCouverts}</span>
                      </div>
                    )}
                    {placesInterieur > 0 && (
                      <div className="flex justify-between items-center py-3 px-4 bg-white rounded-lg">
                        <span className="text-gray-600 flex items-center gap-2">
                          <i className="bi bi-house-door text-blue-500"></i>
                          Places intérieur
                        </span>
                        <span className="font-bold text-gray-900">{placesInterieur}</span>
                      </div>
                    )}
                    {placesTerrasse > 0 && (
                      <div className="flex justify-between items-center py-3 px-4 bg-white rounded-lg">
                        <span className="text-gray-600 flex items-center gap-2">
                          <i className="bi bi-brightness-high text-yellow-500"></i>
                          Places terrasse
                        </span>
                        <span className="font-bold text-gray-900">{placesTerrasse}</span>
                      </div>
                    )}
                    {surfaceTerrasse > 0 && (
                      <div className="flex justify-between items-center py-3 px-4 bg-white rounded-lg">
                        <span className="text-gray-600 flex items-center gap-2">
                          <i className="bi bi-sun text-amber-500"></i>
                          Surface terrasse
                        </span>
                        <span className="font-bold text-gray-900">{formatSurfaceFR(surfaceTerrasse)}</span>
                      </div>
                    )}
                    {loyerMensuel > 0 && (
                      <div className="flex justify-between items-center py-3 px-4 bg-white rounded-lg">
                        <span className="text-gray-600 flex items-center gap-2">
                          <i className="bi bi-calendar-month text-red-500"></i>
                          Loyer mensuel
                        </span>
                        <span className="font-bold text-gray-900">{formatPriceFR(loyerMensuel)}</span>
                      </div>
                    )}
                    {charges > 0 && (
                      <div className="flex justify-between items-center py-3 px-4 bg-white rounded-lg">
                        <span className="text-gray-600 flex items-center gap-2">
                          <i className="bi bi-lightning text-yellow-600"></i>
                          Charges mensuelles
                        </span>
                        <span className="font-bold text-gray-900">{formatPriceFR(charges)}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* 3. Équipements & Prestations */}
                {Object.keys(packsActifs).filter(packId => packsActifs[packId]).length > 0 && (
                  <div className="mt-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <i className="bi bi-star-fill text-yellow-500"></i>
                      Équipements & Prestations
                    </h2>
                    
                    {Object.keys(packsActifs).filter(packId => packsActifs[packId]).map(packId => {
                      const packData = getPackData(packId);
                      if (!packData) return null;
                      
                      return (
                        <div key={packId} className="mb-6">
                          <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                            <i className={`bi ${packData.icon} text-${packData.color}-500`}></i>
                            {packData.label}
                          </h3>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {packData.champs.map(field => {
                              const value = getPackFieldValue(field, property);
                              
                              // Ne pas afficher les champs vides
                              if (value === undefined || value === null || value === '' || value === false) {
                                return null;
                              }
                              
                              return (
                                <div key={field.id} className="flex justify-between items-center py-3 px-4 bg-gray-50 rounded-lg">
                                  <span className="text-gray-700 font-medium">{field.label}</span>
                                  <span className="text-gray-900 font-semibold">
                                    {field.type === 'boolean' && value === true ? (
                                      <i className="bi bi-check-circle-fill text-green-500 text-xl"></i>
                                    ) : field.type === 'number' ? (
                                      `${value} ${field.unite || ''}`
                                    ) : (
                                      value
                                    )}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {/* 4. Détail financier */}
                {(netVendeur > 0 || honoraires > 0) && (
                  <div className="mt-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <i className="bi bi-calculator text-emerald-600"></i>
                      Détail financier
                    </h2>
                    <div className="bg-emerald-50 p-6 rounded-xl border border-emerald-200">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {prix > 0 && (
                          <div className="text-center">
                            <div className="text-sm text-gray-600 mb-1">{typePrix}</div>
                            <div className="text-2xl font-bold text-emerald-700">{formatPriceFR(prix)}</div>
                          </div>
                        )}
                        {netVendeur > 0 && (
                          <div className="text-center">
                            <div className="text-sm text-gray-600 mb-1">Net vendeur</div>
                            <div className="text-2xl font-bold text-gray-900">{formatPriceFR(netVendeur)}</div>
                          </div>
                        )}
                        {honoraires > 0 && (
                          <div className="text-center">
                            <div className="text-sm text-gray-600 mb-1">Honoraires</div>
                            <div className="text-2xl font-bold text-blue-700">{formatPriceFR(honoraires)}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Colonne contact */}
              <div className="lg:col-span-1">
                <div className="bg-gray-50 rounded-xl p-6 sticky top-4">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">
                    Intéressé par ce bien ?
                  </h3>
                  
                  <div className="bg-white rounded-lg p-6 mb-4 text-center">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <i className="bi bi-building text-3xl text-blue-600"></i>
                    </div>
                    <div className="font-bold text-lg text-gray-900 mb-1">Votre Agence</div>
                    <div className="text-sm text-gray-600 mb-4">Experts en immobilier commercial</div>
                    
                    <button
                      onClick={() => setShowContactModal(true)}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      <i className="bi bi-envelope-fill"></i>
                      Contacter l'agence
                    </button>
                  </div>

                  <div className="space-y-3 mb-6">
                    <a href="tel:0123456789" className="flex items-center gap-3 text-gray-700 hover:text-blue-600">
                      <i className="bi bi-telephone-fill text-blue-600"></i>
                      <span>01 23 45 67 89</span>
                    </a>
                    <a href="mailto:contact@agence.fr" className="flex items-center gap-3 text-gray-700 hover:text-blue-600">
                      <i className="bi bi-envelope-fill text-blue-600"></i>
                      <span>contact@agence.fr</span>
                    </a>
                    <div className="flex items-center gap-3 text-gray-700">
                      <i className="bi bi-globe text-blue-600"></i>
                      <span>votre-agence.fr</span>
                    </div>
                  </div>

                  <button className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg mb-3 flex items-center justify-center gap-2">
                    <i className="bi bi-telephone-fill"></i>
                    Appeler maintenant
                  </button>
                  
                  <button className="w-full bg-white hover:bg-gray-50 text-blue-600 font-semibold py-3 rounded-lg border-2 border-blue-600 flex items-center justify-center gap-2">
                    <i className="bi bi-envelope-fill"></i>
                    Envoyer un email
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Modal de contact */}
      {showContactModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Contacter l'agence</h2>
                <button
                  onClick={() => setShowContactModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <i className="bi bi-x-lg text-2xl"></i>
                </button>
              </div>
              
              {contactSuccess ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="bi bi-check-circle-fill text-3xl text-green-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Message envoyé !</h3>
                  <p className="text-gray-600">L'agence vous contactera dans les plus brefs délais.</p>
                </div>
              ) : (
                <form onSubmit={handleContactSubmit}>
                  {/* Info du bien */}
                  <div className="bg-blue-50 p-4 rounded-lg mb-6">
                    <div className="text-sm text-gray-600 mb-1">Bien concerné</div>
                    <div className="font-bold text-gray-900">{titre}</div>
                    {numeroMandat && (
                      <div className="text-sm text-gray-600">Réf: {numeroMandat}</div>
                    )}
                  </div>
                  
                  {/* Nom */}
                  <div className="mb-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Nom complet <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={contactForm.nom}
                      onChange={(e) => setContactForm({...contactForm, nom: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Votre nom"
                      required
                    />
                  </div>
                  
                  {/* Email */}
                  <div className="mb-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="votre@email.com"
                      required
                    />
                  </div>
                  
                  {/* Téléphone */}
                  <div className="mb-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Téléphone
                    </label>
                    <input
                      type="tel"
                      value={contactForm.telephone}
                      onChange={(e) => setContactForm({...contactForm, telephone: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="06 12 34 56 78"
                    />
                  </div>
                  
                  {/* Type de demande */}
                  <div className="mb-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Type de demande
                    </label>
                    <select
                      value={contactForm.type_demande}
                      onChange={(e) => setContactForm({...contactForm, type_demande: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="visite">Demander une visite</option>
                      <option value="question">Poser une question</option>
                      <option value="info">Demander plus d'informations</option>
                      <option value="autre">Autre</option>
                    </select>
                  </div>
                  
                  {/* Message */}
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Votre message <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={contactForm.message}
                      onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      rows="4"
                      placeholder="Décrivez votre demande..."
                      required
                    ></textarea>
                  </div>
                  
                  {/* Boutons */}
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setShowContactModal(false)}
                      className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      disabled={sendingContact}
                      className="flex-1 px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:bg-blue-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {sendingContact ? (
                        <>
                          <i className="bi bi-hourglass-split animate-spin"></i>
                          Envoi...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-send-fill"></i>
                          Envoyer
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SharedPropertyDetail;
